/**
 * 商品マスター画面 (Phase J 重層化版)。
 *
 * 構造: ブランド (メーカー) × バージョン (価格表) のツリー UI。
 *  - 同 brand 内で 1 つの version だけが「適用中 (isActive=true)」
 *  - 「適用中」のカタログから見積/個別説明記録の商品ピッカーが引く
 *  - 過去 version は履歴として温存 (見積行はスナップショットなので影響なし)
 *
 * 操作:
 *  - 各カタログの ○/● で適用/非適用切替 (排他、トランザクション保証)
 *  - 「+ Excel/CSV から取込」: ブランド・バージョン名を入力 → 新規カタログとして投入
 *  - 編集: メタ (label/notes) 更新
 *  - 削除: 適用中以外のみ
 *  - クリック: 右ペインに items 一覧を表示
 */
import {
  Boxes,
  Check,
  CheckCircle2,
  ChevronRight,
  Circle,
  Edit,
  FileSpreadsheet,
  Plus,
  Trash2,
  Upload,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { CatalogCommissionPairDialog } from './dialogs/CatalogCommissionPairDialog';
import { CatalogCsvOverwriteDialog } from './dialogs/CatalogCsvOverwriteDialog';
import { CatalogEditorDialog } from './dialogs/CatalogEditorDialog';
import { CatalogItemEditorDialog } from './dialogs/CatalogItemEditorDialog';
import { ImportProductsDialog } from './dialogs/ImportProductsDialog';

import { EmptyState } from '@/components/common/EmptyState';
import { Screen } from '@/components/layout/Screen';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { useFeatureActions } from '@/features/permissions/usePermission';
import {
  useActivateProductCatalog,
  useCatalogItems,
  useDeleteCatalogItem,
  useDeleteProductCatalog,
  useProductCatalogs,
  type CatalogItemRecord,
  type ProductCatalogRecord,
} from '@/hooks/useProductCatalogs';


export function ProductCatalogsPage() {
  const { data: catalogs, isLoading } = useProductCatalogs();
  const activate = useActivateProductCatalog();
  const remove = useDeleteProductCatalog();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorTarget, setEditorTarget] =
    useState<ProductCatalogRecord | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  // v3.2.20: カタログ active 化後に「報酬設定セットも切り替えますか?」ダイアログを発火
  const [pairDialogState, setPairDialogState] = useState<{
    open: boolean;
    catalogLabel: string;
  }>({ open: false, catalogLabel: '' });

  const grouped = useMemo(() => {
    const map = new Map<string, ProductCatalogRecord[]>();
    for (const c of catalogs ?? []) {
      const arr = map.get(c.category) ?? [];
      arr.push(c);
      map.set(c.category, arr);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [catalogs]);

  // 右ペインに渡す選択中カタログの実体 (CSV 一括上書きの確認文言でブランド名を出すため)
  const selectedCatalog = useMemo(
    () => (catalogs ?? []).find((c) => c.id === selectedId) ?? null,
    [catalogs, selectedId],
  );

  const handleActivate = async (id: string) => {
    try {
      await activate.mutateAsync(id);
      toast.success('適用中カタログを切替えました');
      // v3.2.20: 切替成功後に「報酬設定も切り替える?」ダイアログを発火
      const target = (catalogs ?? []).find((c) => c.id === id);
      const label = target
        ? `${target.category} / ${target.label || target.version}`
        : id;
      setPairDialogState({ open: true, catalogLabel: label });
    } catch (err) {
      toast.error(`切替失敗: ${(err as Error).message}`);
    }
  };

  const handleDelete = async (c: ProductCatalogRecord) => {
    if (
      !window.confirm(
        `カタログ「${c.category} / ${c.label || c.version}」を削除しますか?\n\nサブコレクション items も全削除されます。\n見積等で過去にスナップショット済の行は影響を受けません。`,
      )
    )
      return;
    try {
      await remove.mutateAsync({ id: c.id });
      toast.success('カタログを削除しました');
      if (selectedId === c.id) setSelectedId(null);
    } catch (err) {
      toast.error(`削除失敗: ${(err as Error).message}`);
    }
  };

  return (
    <Screen
      title="商品マスター"
      description="ブランド (メーカー) × 価格表バージョンの 2 軸管理。各ブランド内で 1 つの版だけが「適用中」となり、見積/記録の商品ピッカーから参照されます。"
      actions={
        <>
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              setEditorTarget(null);
              setEditorOpen(true);
            }}
          >
            <Plus />
            空カタログ作成
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={() => setImportOpen(true)}
          >
            <Upload />
            Excel / CSV から取込
          </Button>
        </>
      }
    >
      {isLoading ? (
        <Spinner />
      ) : !catalogs || catalogs.length === 0 ? (
        <EmptyState
          icon={Boxes}
          title="まだカタログがありません"
          description="「Excel / CSV から取込」ボタンから新規カタログを作成してください。"
        />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1.8fr] gap-4">
          {/* 左: カテゴリ × バージョン ツリー */}
          <div className="space-y-4">
            {grouped.map(([category, list]) => (
              <CategoryGroup
                key={category}
                category={category}
                catalogs={list}
                selectedId={selectedId}
                onSelect={setSelectedId}
                onActivate={handleActivate}
                onEdit={(c) => {
                  setEditorTarget(c);
                  setEditorOpen(true);
                }}
                onDelete={handleDelete}
                activatePending={activate.isPending}
              />
            ))}
          </div>

          {/* 右: 選択中カタログの items */}
          <div>
            {selectedCatalog ? (
              <CatalogItemsPanel
                catalogId={selectedCatalog.id}
                catalog={selectedCatalog}
              />
            ) : (
              <div className="rounded-md border border-[var(--border)] bg-[var(--bg-surface)] p-8 text-center">
                <FileSpreadsheet className="size-8 mx-auto text-[var(--text-muted)] mb-2" />
                <p className="text-sm text-[var(--text-muted)]">
                  左のカタログを選択すると、内訳を表示します
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      <CatalogEditorDialog
        open={editorOpen}
        onOpenChange={setEditorOpen}
        existing={editorTarget}
      />
      <ImportProductsDialog open={importOpen} onOpenChange={setImportOpen} />
      {/* v3.2.20: カタログ切替後に報酬設定セットも切り替えるか問い合わせ */}
      <CatalogCommissionPairDialog
        open={pairDialogState.open}
        onOpenChange={(open) =>
          setPairDialogState((s) => ({ ...s, open }))
        }
        direction="catalog-to-commission"
        primaryEntityLabel={pairDialogState.catalogLabel}
      />
    </Screen>
  );
}

// ─────────────────────────────────────────────
// カテゴリグループ
// ─────────────────────────────────────────────

function CategoryGroup({
  category,
  catalogs,
  selectedId,
  onSelect,
  onActivate,
  onEdit,
  onDelete,
  activatePending,
}: {
  category: string;
  catalogs: ProductCatalogRecord[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onActivate: (id: string) => void;
  onEdit: (c: ProductCatalogRecord) => void;
  onDelete: (c: ProductCatalogRecord) => void;
  activatePending: boolean;
}) {
  return (
    <div className="rounded-md border border-[var(--border)] bg-[var(--bg-surface)] p-3">
      <div className="flex items-center gap-2 mb-3">
        <Boxes className="size-4 text-[var(--accent)]" />
        <h3 className="text-sm font-semibold text-[var(--text-primary)]">
          {category}
        </h3>
        <span className="text-[10px] text-[var(--text-muted)]">
          {catalogs.length} バージョン
        </span>
      </div>
      <div className="space-y-1.5">
        {catalogs.map((c) => {
          const selected = c.id === selectedId;
          return (
            <div
              key={c.id}
              className={`group flex items-center gap-2 px-2 py-2 rounded-md border transition-colors ${
                selected
                  ? 'border-[var(--accent)] bg-[var(--accent-subtle)]'
                  : 'border-[var(--border)] bg-[var(--bg-elevated)] hover:bg-[var(--bg-hover)]'
              }`}
            >
              {/* ラジオ風 active トグル */}
              <button
                type="button"
                onClick={() => !c.isActive && onActivate(c.id)}
                disabled={c.isActive || activatePending}
                title={c.isActive ? '適用中' : 'このバージョンを適用'}
                className={`flex-shrink-0 ${
                  c.isActive
                    ? 'text-[var(--accent-green)]'
                    : 'text-[var(--text-muted)] hover:text-[var(--accent)]'
                }`}
              >
                {c.isActive ? (
                  <CheckCircle2 className="size-4" />
                ) : (
                  <Circle className="size-4" />
                )}
              </button>
              {/* メイン */}
              <button
                type="button"
                onClick={() => onSelect(c.id)}
                className="flex-1 text-left min-w-0"
              >
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-[var(--text-primary)] truncate">
                    {c.label || c.version}
                  </span>
                  {c.isActive && (
                    <Badge variant="approved">適用中</Badge>
                  )}
                </div>
                <div className="text-[10px] text-[var(--text-muted)] mt-0.5">
                  {c.version} · {c.itemCount} 件
                  {c.notes ? ` · ${c.notes}` : ''}
                </div>
              </button>
              {/* 操作 */}
              <button
                type="button"
                onClick={() => onEdit(c)}
                className="p-1 text-[var(--text-muted)] hover:text-[var(--accent)] opacity-0 group-hover:opacity-100"
                title="編集"
              >
                <Edit className="size-3.5" />
              </button>
              <button
                type="button"
                onClick={() => onDelete(c)}
                disabled={c.isActive}
                className="p-1 text-[var(--text-muted)] hover:text-[var(--accent-red)] opacity-0 group-hover:opacity-100 disabled:opacity-30 disabled:cursor-not-allowed"
                title={c.isActive ? '適用中は削除不可' : '削除'}
              >
                <Trash2 className="size-3.5" />
              </button>
              <ChevronRight className="size-3 text-[var(--text-muted)] flex-shrink-0" />
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// 右ペイン: items
// ─────────────────────────────────────────────

function CatalogItemsPanel({
  catalogId,
  catalog,
}: {
  catalogId: string;
  catalog: ProductCatalogRecord | null;
}) {
  const items = useCatalogItems(catalogId);
  const deleteItem = useDeleteCatalogItem();
  const acts = useFeatureActions('master.products');
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [csvOverwriteOpen, setCsvOverwriteOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<CatalogItemRecord | null>(
    null,
  );

  const handleAdd = () => {
    setEditingItem(null);
    setItemDialogOpen(true);
  };

  const handleEdit = (it: CatalogItemRecord) => {
    setEditingItem(it);
    setItemDialogOpen(true);
  };

  const handleDelete = async (it: CatalogItemRecord) => {
    if (
      !window.confirm(`商品「${it.name}」(${it.sku}) を削除しますか？`)
    )
      return;
    try {
      await deleteItem.mutateAsync({ catalogId, itemId: it.id });
      toast.success('商品を削除しました');
    } catch (err) {
      toast.error(`削除失敗: ${(err as Error).message}`);
    }
  };

  return (
    <div className="rounded-md border border-[var(--border)] bg-[var(--bg-surface)]">
      <div className="px-4 py-2.5 border-b border-[var(--border)] flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)]">
            内訳商品
          </h4>
          <span className="text-[10px] text-[var(--text-muted)]">
            {items.data?.length ?? 0} 件
          </span>
        </div>
        <div className="flex items-center gap-2">
          {acts.edit && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setCsvOverwriteOpen(true)}
              title="このカタログの内訳商品を Excel/CSV で差し替える"
            >
              <Upload className="size-3.5 mr-1" />
              CSV 一括上書き
            </Button>
          )}
          <Button type="button" size="sm" variant="secondary" onClick={handleAdd}>
            <Plus className="size-3.5 mr-1" />
            商品を追加
          </Button>
        </div>
      </div>
      {items.isLoading ? (
        <div className="py-8">
          <Spinner />
        </div>
      ) : !items.data || items.data.length === 0 ? (
        <div className="py-8 px-4 text-center text-xs text-[var(--text-muted)]">
          このカタログには内訳がありません。
          <br />
          「商品を追加」または「CSV 一括上書き」から投入してください。
        </div>
      ) : (
        <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-[var(--bg-elevated)]">
              <tr className="text-left text-[var(--text-muted)] uppercase tracking-wider border-b border-[var(--border)]">
                <th className="px-3 py-2 font-medium">SKU</th>
                <th className="px-3 py-2 font-medium">商品名</th>
                <th className="px-3 py-2 font-medium">製品カテゴリ</th>
                <th className="px-3 py-2 font-medium text-right">仕入価格</th>
                <th className="px-3 py-2 font-medium text-right">税抜</th>
                <th className="px-3 py-2 font-medium text-right">税込</th>
                <th className="px-3 py-2 font-medium w-16"></th>
              </tr>
            </thead>
            <tbody>
              {items.data.map((it, i) => (
                <tr
                  key={it.id}
                  className={`group border-b border-[var(--border)] last:border-b-0 ${i % 2 === 0 ? '' : 'bg-[var(--bg-elevated)]'}`}
                >
                  <td className="px-3 py-1.5 font-mono text-[10px] text-[var(--text-muted)]">
                    {it.sku}
                  </td>
                  <td className="px-3 py-1.5">
                    <div className="text-[var(--text-primary)]">{it.name}</div>
                    {it.description && (
                      <div className="text-[10px] text-[var(--text-muted)] mt-0.5 line-clamp-1">
                        {it.description}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-1.5">
                    {it.productCategory ? (
                      <Badge variant="default">{it.productCategory}</Badge>
                    ) : (
                      <span className="text-[10px] text-[var(--text-muted)]">
                        —
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-[var(--text-muted)]">
                    ¥{it.costPrice.toLocaleString('ja-JP')}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums">
                    ¥{it.priceExcludingTax.toLocaleString('ja-JP')}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums font-medium">
                    ¥{it.priceIncludingTax.toLocaleString('ja-JP')}
                  </td>
                  <td className="px-1 py-1.5 text-right whitespace-nowrap">
                    <button
                      type="button"
                      onClick={() => handleEdit(it)}
                      className="p-1 text-[var(--text-muted)] hover:text-[var(--accent)] opacity-0 group-hover:opacity-100"
                      title="編集"
                    >
                      <Edit className="size-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(it)}
                      disabled={deleteItem.isPending}
                      className="p-1 text-[var(--text-muted)] hover:text-[var(--accent-red)] opacity-0 group-hover:opacity-100 disabled:opacity-30"
                      title="削除"
                    >
                      <Trash2 className="size-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <CatalogItemEditorDialog
        open={itemDialogOpen}
        onOpenChange={setItemDialogOpen}
        catalogId={catalogId}
        existing={editingItem}
      />
      <CatalogCsvOverwriteDialog
        open={csvOverwriteOpen}
        onOpenChange={setCsvOverwriteOpen}
        catalogId={catalogId}
        catalog={catalog}
      />
    </div>
  );
}

// 未使用 import (型推論のため残す) を避ける
void Check;
